import { useState, useMemo, useCallback } from 'react';
import { Menu, MenuItem } from '@mui/material';
import { DateTime } from 'luxon';
import {
  DocketMobileCard,
  DocketMobilePagination,
  FlexContainer,
} from '@act/storybook';
import { PaginationBounds } from 'toolkit/tables/types/table.types';
import { useAppNavigation } from 'react-components/app/routing/routes';
import { NeedDto } from '@act/shared/data-transfer-objects';
import { metaSliceSelectors } from 'redux/slices/metaSlice.hooks';
import useAddActionDialog from '@act/react-components/dialog-forms/hooks/use-add-action-dialog';
import { NeedDtoWithMember } from './DocketNeedsTableView';
import { needIsExpiringSoon } from '@act/storybook';

export interface DocketNeedsMobileViewProps {
  needs: NeedDtoWithMember[];
  totalItems: number;
  bounds: PaginationBounds;
  setBounds: React.Dispatch<React.SetStateAction<PaginationBounds>>;
  onExtendTimeWindow: (needId: number) => void;
}

export default function DocketNeedsMobileView({
  needs,
  totalItems,
  bounds,
  setBounds,
  onExtendTimeWindow,
}: DocketNeedsMobileViewProps) {
  const navigate = useAppNavigation();
  const needIssueOptions = metaSliceSelectors.useSelectNeedIssueOptions();
  const openAddActionDialog = useAddActionDialog();

  // --- 1. UI State (Mobile Menu) ---
  const [mobileMenuAnchor, setMobileMenuAnchor] = useState<HTMLElement | null>(
    null
  );
  const [selectedMobileItem, setSelectedMobileItem] =
    useState<NeedDtoWithMember | null>(null);

  // --- 2. Mobile Menu Handlers (Purely UI) ---
  const handleOpenMobileMenu = useCallback(
    (event: React.MouseEvent<HTMLElement>, item: NeedDtoWithMember) => {
      setMobileMenuAnchor(event.currentTarget);
      setSelectedMobileItem(item);
    },
    []
  );

  const handleCloseMobileMenu = useCallback(() => {
    setMobileMenuAnchor(null);
    setSelectedMobileItem(null);
  }, []);

  const handleMobileMenuItemClick = useCallback(
    async (callback: () => void) => {
      if (selectedMobileItem) {
        await callback();
      }
      handleCloseMobileMenu();
    },
    [selectedMobileItem, handleCloseMobileMenu]
  );

  // --- 3. Helper Functions ---
  const getTaskText = (need: NeedDto) => {
    return needIsExpiringSoon(need) ? 'Expiring Soon' : 'Needs Action';
  };

  const getIssueText = (need: NeedDto) => {
    const issue = needIssueOptions.find(issue => issue.value === need.issueId);
    return issue?.label || 'Unknown Issue';
  };

  const formatDueDate = (timestamp: number) => {
    if (!timestamp) return '––';
    return DateTime.fromMillis(timestamp).toFormat('LLL dd, yyyy');
  };

  // --- 4. Menu Items ---
  const getMenuItems = (need: NeedDtoWithMember) => {
    const items = [
      {
        display: 'View need details',
        callback: () =>
          navigate.toMemberPage(
            need.member?.assignedGuideId,
            need.memberId,
            undefined,
            need.id.toString()
          ),
      },
      {
        display: 'Add action',
        callback: () => {
          openAddActionDialog({ need });
        },
      },
    ];

    if (needIsExpiringSoon(need)) {
      items.push({
        display: 'Extend time window',
        callback: () => {
          onExtendTimeWindow(need.id);
        },
      });
    }

    return items;
  };

  const visibleMenuItems = useMemo(() => {
    if (!selectedMobileItem) return [];
    return getMenuItems(selectedMobileItem);
  }, [selectedMobileItem, needIssueOptions]);

  // --- 5. Render Logic ---
  return (
    <FlexContainer
      direction="column"
      gap={2}
      width="100%"
      padding="16px 0 0 0"
      height="auto"
      sx={{
        width: '100%',
        maxWidth: '100%',
        minWidth: 0,
        boxSizing: 'border-box',
      }}
    >
      {/* Render Mobile Cards */}
      {needs.map(need => {
        const handleGotoAction = () => {
          navigate.toMemberPage(
            need.member?.assignedGuideId,
            need.memberId,
            undefined,
            need.id.toString()
          );
        };

        return (
          <DocketMobileCard
            key={need.id}
            member={need.member}
            topic={`${getIssueText(need)} - ${getTaskText(need)}`}
            date={formatDueDate(need.expiresAt)}
            time=""
            gotoAction={handleGotoAction}
            openMenu={e => handleOpenMobileMenu(e, need)}
          />
        );
      })}

      {/* Render Mobile Pagination */}
      <DocketMobilePagination
        page={bounds.pageStart}
        pageSize={bounds.pageSize}
        totalItems={totalItems}
        onPageChange={(newPage: number) => {
          setBounds(prevBounds => ({
            ...prevBounds,
            pageStart: newPage,
          }));
        }}
      />

      {/* Render Mobile Menu */}
      <Menu
        anchorEl={mobileMenuAnchor}
        open={Boolean(mobileMenuAnchor)}
        onClose={handleCloseMobileMenu}
      >
        {visibleMenuItems.map((item, index) => (
          <MenuItem
            key={index}
            onClick={() => handleMobileMenuItemClick(item.callback)}
          >
            {item.display}
          </MenuItem>
        ))}
      </Menu>
    </FlexContainer>
  );
}
